/**
 * Line items extraction with images from Shopify products
 */

import { buildVariantIndex, type VariantIndexItem } from "./recharge";

export type LineItemWithImage = {
  variantId: string;
  productTitle: string;
  variantTitle: string;
  quantity: number;
  price?: string;
  imageUrl: string | null;
  productId: string;
  isBundle?: boolean;
  subItems?: LineItemWithImage[]; // Nested meals for bundle products
};

/**
 * Check if a product title indicates it's a bundle/plan product
 */
function isBundleProduct(title: string): boolean {
  const lower = title.toLowerCase();
  return lower.includes("day plan") || lower.includes("bundle") || lower.includes("box");
}

/**
 * Check if a product is a treat (by tags or title patterns)
 */
function isTreatProduct(info: VariantIndexItem | undefined, title: string): boolean {
  // Check by tags first
  if (info?.tags?.some(t => t.toLowerCase() === "treats")) {
    return true;
  }
  // Check by title patterns
  const lower = title.toLowerCase();
  return (
    lower.includes("treat") ||
    lower.includes("stick") ||
    lower.includes("strip") ||
    lower.includes("pizzle") ||
    lower.includes("biscuit") ||
    lower.includes("snack") ||
    lower.includes("chew")
  );
}

/**
 * Extract line items from an order/charge with images
 * If subscriptions are provided, will extract meals from bundle subscriptions as nested subItems
 */
export function extractLineItemsWithImages(
  orderOrCharge: any,
  products: any[],
  subscriptions: any[] = []
): LineItemWithImage[] {
  const variantIndex = buildVariantIndex(products);
  const lineItems = orderOrCharge?.line_items || [];
  const result: LineItemWithImage[] = [];

  // Build a map of subscription_id -> subscription for quick lookup
  const subsById = new Map<string, any>();
  for (const sub of subscriptions) {
    subsById.set(String(sub.id), sub);
  }

  for (const item of lineItems) {
    const variantId = String(item?.variant_id || item?.shopify_variant_id || "");
    if (!variantId) continue;

    const quantity = Number(item?.quantity || 1);
    const price = item?.price || item?.unit_price;
    const variantInfo = variantIndex.get(variantId);
    const productTitle = variantInfo?.productTitle || item?.product_title || item?.title || "Product";

    // Check if this is a bundle product
    if (isBundleProduct(productTitle)) {
      const bundleItem: LineItemWithImage = {
        variantId,
        productTitle,
        variantTitle: variantInfo?.variantTitle || item?.variant_title || "",
        quantity,
        price: price ? String(price) : undefined,
        imageUrl: variantInfo?.imageUrl || null,
        productId: variantInfo?.productId || "",
        isBundle: true,
        subItems: [],
      };

      // Try to find the subscription and extract meals as subItems
      const subscriptionId = String(item?.subscription_id || "");
      const subscription = subsById.get(subscriptionId);

      if (subscription) {
        // Extract meals from _paws_meals_json property
        const mealsJsonProp = subscription.properties?.find(
          (p: any) => p.name === "_paws_meals_json"
        );

        if (mealsJsonProp?.value) {
          try {
            const meals = JSON.parse(mealsJsonProp.value);
            if (Array.isArray(meals) && meals.length > 0) {
              for (const meal of meals) {
                const mealVariantId = String(meal.variantId || "");
                const mealVariantInfo = variantIndex.get(mealVariantId);
                const mealTitle = mealVariantInfo?.productTitle || meal.productTitle || meal.title || "Meal";
                const mealQuantity = Number(meal.count || meal.quantity || 1);

                // Check if this is a treat - if so, add as separate line item
                if (isTreatProduct(mealVariantInfo, mealTitle)) {
                  // Add treat as separate line item (will be grouped in "Treats & Add-ons")
                  result.push({
                    variantId: mealVariantId,
                    productTitle: mealTitle,
                    variantTitle: mealVariantInfo?.variantTitle || "",
                    quantity: mealQuantity,
                    imageUrl: mealVariantInfo?.imageUrl || null,
                    productId: mealVariantInfo?.productId || "",
                  });
                } else {
                  // Add as bundle subItem (meal)
                  bundleItem.subItems!.push({
                    variantId: mealVariantId,
                    productTitle: mealTitle,
                    variantTitle: mealVariantInfo?.variantTitle || "",
                    quantity: mealQuantity,
                    imageUrl: mealVariantInfo?.imageUrl || null,
                    productId: mealVariantInfo?.productId || "",
                  });
                }
              }
            }
          } catch (e) {
            // Failed to parse meals JSON, bundle will have empty subItems
          }
        }
      }

      result.push(bundleItem);
    } else {
      // Regular product
      result.push({
        variantId,
        productTitle,
        variantTitle: variantInfo?.variantTitle || item?.variant_title || "",
        quantity,
        price: price ? String(price) : undefined,
        imageUrl: variantInfo?.imageUrl || null,
        productId: variantInfo?.productId || "",
      });
    }
  }

  return result;
}

/**
 * Group line items by bundles/meals/treats/other
 * - bundles: Bundle products with nested subItems (meals)
 * - meals: Individual meal products (non-bundle)
 * - treats: Treats and add-ons
 * - other: Everything else
 */
export function groupLineItems(items: LineItemWithImage[]) {
  const bundles: LineItemWithImage[] = []; // Bundle products with nested meals
  const meals: LineItemWithImage[] = []; // Individual meals (non-bundle)
  const treats: LineItemWithImage[] = [];
  const other: LineItemWithImage[] = [];

  for (const item of items) {
    const title = item.productTitle.toLowerCase();

    // Bundle products go to bundles section (with their nested subItems)
    if (item.isBundle) {
      bundles.push(item);
      continue;
    }

    // Check if it's a treat FIRST (before meals, since some treats contain "beef", "salmon", etc.)
    if (title.includes("treat") ||
        title.includes("biscuit") ||
        title.includes("snack") ||
        title.includes("stick") ||
        title.includes("strip") ||
        title.includes("pizzle") ||
        title.includes("chew")) {
      treats.push(item);
    }
    // Check if it's a meal/recipe (individual meal pouches)
    else if (title.includes("chicken") ||
        title.includes("beef") ||
        title.includes("turkey") ||
        title.includes("lamb") ||
        (title.includes("salmon") && !title.includes("strip")) ||
        title.includes("fish") ||
        title.includes("recipe") ||
        title.includes("meal") ||
        title.includes("pork") ||
        title.includes("duck") ||
        title.includes("venison")) {
      meals.push(item);
    }
    // Everything else
    else {
      other.push(item);
    }
  }

  return { bundles, meals, treats, other };
}
